import { TypedDocumentNode, gql, useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, DisplayS } from '@sorare/core/src/atoms/typography';
import { InteractiveStackedCards } from '@sorare/core/src/components/cards/InteractiveStackedCards';
import { commonCardMarketplaceFragment } from '@sorare/core/src/contexts/currentUser/queries';
import { useIsMediumMobileHeightAndAbove } from '@sorare/core/src/hooks/device/useIsMediumMobileHeightAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';
import { mediumMobileHeightAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useNBAEvents } from '@sorare/gameplay/src/lib/events';

import {
  NBAOnboardingCommonCardDeckMutation,
  NBAOnboardingCommonCardDeckMutationVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  flex: 1;
  position: relative;
  gap: var(--double-unit);
  justify-content: space-between;

  & > * {
    z-index: 1;
    position: relative;
  }
`;

const TextContent = styled(Vertical)`
  display: flex;
  position: relative;
  align-items: center;

  @media ${mediumMobileHeightAndAbove} {
    margin-top: var(--triple-unit);
  }
  padding-inline: var(--double-unit);
`;

const ButtonWrapper = styled.div`
  width: 100%;
  padding: var(--double-unit);
  display: flex;
  gap: var(--unit);
`;

const mutation = gql`
  mutation NBAOnboardingCommonCardDeckMutation(
    $teamSlug: String!
    $playerSlug: String!
    $sport: Sport!
  ) {
    claimStarterPack(
      input: {
        sport: NBA
        favoriteTeamSlug: $teamSlug
        starPlayerSlug: $playerSlug
      }
    ) {
      errors {
        message
      }
      cards {
        slug
        pictureUrl
      }
      currentUser {
        slug
        ...commonCardMarketplace_currentUser
      }
    }
  }
  ${commonCardMarketplaceFragment}
` as TypedDocumentNode<
  NBAOnboardingCommonCardDeckMutation,
  NBAOnboardingCommonCardDeckMutationVariables
>;

type Props = {
  teamSlug: string;
  playerSlug: string;
  onClose: () => void;
};

export const CardDeck = ({ onClose, playerSlug, teamSlug }: Props) => {
  const track = useNBAEvents();
  const [claimStarterPack, { data }] = useMutation(mutation, {
    variables: { playerSlug, teamSlug, sport: Sport.NBA },
  });
  const isMediumMobileHeightAndAbove = useIsMediumMobileHeightAndAbove();

  useEffect(() => {
    claimStarterPack();
    track('View Onboarding Starter pack content');
  }, [claimStarterPack, track]);

  return (
    <Wrapper>
      <TextContent>
        <DisplayS brand className="text-center">
          <FormattedMessage
            id="StarterPack.title"
            defaultMessage="Starter pack"
          />
        </DisplayS>
        <BodyM color="var(--c-neutral-600)" className="text-center">
          <FormattedMessage
            id="StarterPack.subtitle"
            defaultMessage="You’re ready to play! Check out your cards, and get ready to dominate."
          />
        </BodyM>
      </TextContent>
      <InteractiveStackedCards
        restack
        cards={
          data?.claimStarterPack?.cards
            ?.map(card => card.pictureUrl)
            .filter(Boolean) || []
        }
        cardWidth={isMediumMobileHeightAndAbove ? 240 : 180}
      />
      <ButtonWrapper>
        <Button
          onClick={() => {
            track('Click close Onboarding');
            onClose();
          }}
          color="secondary"
          fullWidth
          size="large"
        >
          <FormattedMessage {...glossary.continue} />
        </Button>
      </ButtonWrapper>
    </Wrapper>
  );
};
